import { Controller } from "@hotwired/stimulus";

const SCROLL_OFFSET = 0;

export class ScrollSpyController extends Controller {
  static targets = ["link", "indicator", "ghost", "section"];

  connect() {
    this.linkTargets.forEach((link) => {
      link.addEventListener("mouseenter", () => this.showGhost(link));
      link.addEventListener("mouseleave", () => this.hideGhost());
    });

    this.navbar = document.querySelector("nav.navbar");
    this.updateNavbarHeight();
    this.buildObserver();

    this.onResize = () => this.scheduleReposition();
    window.addEventListener("resize", this.onResize);
  }

  disconnect() {
    this.observer?.disconnect();
    window.removeEventListener("resize", this.onResize);
    clearTimeout(this.resizeTimer);
    cancelAnimationFrame(this.rafId);
  }

  buildObserver() {
    const navH = (this.navbar?.offsetHeight || 0) + SCROLL_OFFSET;
    const topMargin = -navH;
    const bottomMargin = -(window.innerHeight - navH - 1);

    this.visible = new Set();

    this.observer = new IntersectionObserver(
      (entries) => this.onIntersect(entries),
      { rootMargin: `${topMargin}px 0px ${bottomMargin}px 0px` },
    );

    this.sections = [];
    this.sectionTargets.forEach((section) => {
        this.sections.push(section);
        this.observer.observe(section);
    });
  }

  noop(event) {
    this.dispatch("noop", { cancelable: true })
  }

  onIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) this.visible.add(entry.target);
      else this.visible.delete(entry.target);
    });

    let best = null;
    let bestTop = Infinity;
    this.visible.forEach((section) => {
      const top = section.getBoundingClientRect().top;
      if (top < bestTop) {
        bestTop = top;
        best = section;
      }
    });

    if (best) this.setActive(best.id);
  }

  updateNavbarHeight() {
    if (!this.navbar) return;
    const h = SCROLL_OFFSET + this.navbar.offsetHeight;
    document.documentElement.style.setProperty("--navbar-height", `${h}px`);
  }

  setActive(id) {
    let active = null;
    this.linkTargets.forEach((link) => {
      const linkId = new URL(link.href, window.location.href).hash.slice(1);
      const on = linkId === id;
      link.classList.toggle("active", on);
      if (on) active = link;
    });
    this.activeLink = active;
    this.moveIndicator(active);
  }

  moveIndicator(link) {
    if (!link || !this.hasIndicatorTarget) return;
    this.position(this.indicatorTarget, link);
    this.indicatorTarget.style.backgroundColor =
      link.dataset.color || "currentColor";
  }

  showGhost(link) {
    if (!this.hasGhostTarget) return;
    if (link === this.activeLink) return this.hideGhost();
    this.position(this.ghostTarget, link);
    this.ghostTarget.style.backgroundColor =
      link.dataset.color || "currentColor";
    this.ghostTarget.style.opacity = "0.4";
  }

  scheduleReposition() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      cancelAnimationFrame(this.rafId);
      this.rafId = requestAnimationFrame(() => {
        this.updateNavbarHeight();
        this.moveIndicator(this.activeLink);
      });
    }, 100);
  }

  hideGhost() {
    if (!this.hasGhostTarget) return;
    this.ghostTarget.style.opacity = "0";
  }

  position(el, link) {
    el.style.transform = `translateX(${link.offsetLeft}px)`;
    el.style.width = `${link.offsetWidth}px`;
  }
}
